import { cva as cvaFn, type VariantProps } from "class-variance-authority";
import { twMerge } from "tailwind-merge";
import type { UnionToIntersection } from "type-fest";

import { cssVars, IVarsGenOptions, IVarsGenReturn, ICssVars } from "./cssVars";

type ICvaFnOptions<CvaType> = {
  base?: Parameters<typeof cvaFn<CvaType>>[0];
} & Parameters<typeof cvaFn<CvaType>>[1];

type ICvaFnReturn<CvaType> = ReturnType<typeof cvaFn<CvaType>>;

type IOptions<CssVarsType extends ICssVars, CvaType> = ICvaFnOptions<CvaType> &
  IVarsGenOptions<CssVarsType>;

type IReturn<CssVarTypes extends ICssVars, CvaType> = {
  cls: ICvaFnReturn<CvaType>;
  vars: IVarsGenReturn<CssVarTypes>;
};

/**
 * Tailwind (Slate) Variants
 *
 * It's a merge of class-variance-authority and Slate's cssVars.
 *
 * Difference from cva:
 * - uses a `base` prop instead of the first argument
 * - returns a `cls` and `vars` object instead of a function
 *
 * It has a Slate extension that allows for computing css vars from the theme.
 *
 * **Enabling intelisense** -
 * [instructions](https://www.tailwind-variants.org/docs/getting-started#intellisense-setup-optional)
 *
 * @example
 * ```ts
 * const container = tsv({
 *   vars: {
 *     size: "component-size",
 *   },
 *   defaultVarsValues: {
 *     size: "40px",
 *   },
 *   base: "bg-red-500 w-(--component-size) h-(--component-size)",
 *   variants: {
 *     canSee: {
 *       true: "bg-blue-500",
 *     },
 *     color: {
 *       primary: "text-red-500",
 *       secondary: "text-blue-500",
 *     },
 *   },
 *   defaultVariants: {
 *     color: "primary",
 *   },
 * });
 *
 * const containerCls = container.cls();
 *
 * const containerVars = container.vars({
 *   size: "60px",
 * });
 *
 * <div className={containerCls} style={containerVars}>
 *   Hello
 * </div>
 * ```
 */
export function tsv<
  CssVarTypes extends ICssVars,
  CvaType = Record<string, never>,
>(options: IOptions<CssVarTypes, CvaType>): IReturn<CssVarTypes, CvaType> {
  const { base, vars, defaultVarsValues, ...cvaArgs } = options;
  const cls = cvaFn(base, {
    compoundVariants: cvaArgs.compoundVariants,
    defaultVariants: cvaArgs.defaultVariants,
    variants: cvaArgs.variants,
  } as Parameters<typeof cvaFn<CvaType>>[1]);
  const varsFn = cssVars({ vars, defaultVarsValues });
  const clsFn = (...args: Parameters<typeof cls>) => twMerge(cls(...args));

  return { cls: clsFn, vars: varsFn };
}

/**
 * Any object returned by {@link tsv}. `cls` takes the empty object rather than
 * `IReturn`'s props so that every variant config stays assignable to it.
 */
type IAnyTsv = {
  cls: (props?: Record<never, never>) => string;
  vars: IVarsGenReturn<ICssVars>;
};

/** cva's `class` / `className` prop, as accepted by every `tsv` `cls`. */
type IClassProp = NonNullable<Parameters<ICvaFnReturn<unknown>>[0]>;

/**
 * A `tsv` call declaring no variants (or no `vars`) types its arguments as a
 * string index signature, which would let any key through the merged arguments.
 * Only apply this to arguments — return types keep their index signature so
 * `vars()` stays indexable like {@link tsv}'s.
 */
type DropIndexSignature<P> = P extends unknown
  ? string extends keyof P
    ? Record<never, never>
    : P
  : never;

type IVariantsOf<Style extends IAnyTsv> = Style extends unknown
  ? DropIndexSignature<VariantProps<Style["cls"]>>
  : never;

type IMergedTsv<T extends readonly IAnyTsv[]> = {
  cls: (
    props?: UnionToIntersection<IVariantsOf<T[number]>> & IClassProp
  ) => string;
  vars: (
    cssVars?: UnionToIntersection<
      DropIndexSignature<Parameters<T[number]["vars"]>[0]>
    >
  ) => UnionToIntersection<ReturnType<T[number]["vars"]>>;
};

/**
 * Merges `tsv` objects into one `{ cls, vars }` object.
 *
 * Later objects override earlier ones when class names or CSS variables conflict.
 *
 * @example
 * ```ts
 * const merged = tsvMerge(layoutStyle, colorStyle);
 *
 * const className = merged.cls({ size: "sm", tone: "primary" });
 * const style = merged.vars({ width: "40px", color: "red" });
 * ```
 */
export function tsvMerge<T extends IAnyTsv[]>(...styles: T): IMergedTsv<T> {
  return {
    cls: (props) => twMerge(...styles.map((style) => style.cls(props))),
    vars: (cssVars) =>
      Object.assign({}, ...styles.map((style) => style.vars(cssVars ?? {}))),
  };
}
